import { useMutation } from '@tanstack/react-query'
import type { IResponse, IHttpError } from '../core'
import { ObjectsFactory, Model } from '../core'
import { IFile } from '../file'

export interface ITransaction {
  id: number
  transaction: string
  createDate: string
  updateDate: string
  status: string
  amount: number
  rate: number
  est_amount: number
  total_sponsor_fee: number
  voucher: string
}
export interface ITransactions {
  voucher: any
  total_sponsor_fee: any
  est_amount: any
  rate: any
  amount: any
  status: any
  updateDate: any
  createDate: any
  transaction: any
  data: ITransactionsCompany[] | any
  total: number
}

export interface ITransactionsCompany {
  logo_file: IFile | undefined
  data?: any
  id: string
  name: string
  company_code: string
  total_user: number
  status: string
  last_updated_at?: any
  request_num: string
  request_amount: any
  service_fee: any
  voucher_discount: any
  eligibility_amount?: any
  eligibility_rate?: any
  contract?: ITransactionsCompanyContract
  company?: any
  current_pay_period: string
}

export interface ITransactionsCompanyContract {
  id: number
  created_at: string
  updated_at: string
  created_by: number
  updated_by: number
  company_id: number
  file_id: string
  start_date: string
  end_date: string
  status: string
  file: ITransactionsCompanyContractFile
}
export interface ITransactionsCompanyContractFile {
  id: string
  name: string
  path: string
  object_type: string
  object_id: number
  type: string
  large_path: any
  medium_path: any
  small_path: any
  tiny_path: any
  large_url?: string
  medium_url?: string
  tiny_url?: string
  path_url?: string
  url: string
}

export interface ITransactionEmployeeDetail {
  user: any
  id: number
  created_at: string
  updated_at: string
  created_by: any
  updated_by: any
  code: string
  month: string
  company_id: number
  user_id: number
  bank_id: number
  bank_account_number: string
  bank_account_name: string
  request_amount: number
  service_fee: number
  voucher_discount: number
  transfer_amount: number
  transferred_amount: number
  eligibility_rate: number
  eligibility_amount: number
  worked_day: number
  daily_wage: any
  status: string
  reason: string
  last_checked_at: any
  completed_at: string
  extra: Extra
  // user: Use
  total?: number
  data?: any
  partner_code?: string
  payment_partner?: string
}

export interface Extra {
  infoTransaction: InfoTransaction
}

export interface InfoTransaction {
  status: string
  description: string
  referenceId: string
  responseCode: number
  requestAmount: number
  transactionAmount: number
}

export interface ITransactionDetail {
  id: number
  created_at: string
  updated_at: string
  created_by: any
  updated_by: any
  code: string
  month: string
  company_id: number
  user_id: number
  bank_id: number
  bank_account_number: string
  bank_account_name: string
  request_amount: number
  service_fee: number
  voucher_discount: number
  transfer_amount: number
  transferred_amount: number
  eligibility_rate: number
  eligibility_amount: number
  worked_day: number
  daily_wage: any
  status: string
  reason: string
  last_checked_at: any
  completed_at: string
  extra: ITransactionDetailExtra
  user: ITransactionDetailUser
  working_rate?: any
  bank?: any
}

export interface ITransactionDetailExtra {
  infoTransaction: ITransactionDetailInfo
}

export interface ITransactionDetailInfo {
  status: string
  description: string
  referenceId: string
  responseCode: number
  requestAmount: number
  transactionAmount: number
}

export interface ITransactionDetailUser {
  id: number
  created_at: string
  updated_at: string
  first_name: string
  last_name: any
  system_code: string
  employee_id: string
  status: string
  email: string
  user_type: string
  company_id: number
  avatar: any
  bank_id: number
  bank_account_number: string
  bank_account_name: string
  birth_date: string
  eligibility_rate: number
  eligibility_updated_at: string
  eligibility_updated_by: any
  eligibility_status: string
  tenure: any
  dependent: any
  gender: any
  joined_date: any
  contract_type: any
  identity_card: any
  identity_card_issue_date: any
  identity_card_issue_place: any
  address: any
  phone: string
  phone_code: string
  bank: ITransactionDetailBank
  avatar_file: any
}
export interface ITransactionDetailBank {
  id: number
  created_at: string
  updated_at: string
  name: string
  code: string
  vnpt_epay_code: string
  shortName: string
}

export interface IBulkEligibilityPayload {
  is_all: number
  user_ids?: number[]
  company_ids?: number[]
  exclude_user_ids?: number[]
  exclude_company_ids?: number[]
  eligibility_rate: number
}

export interface ITransactionStatusPayload {
  eligibility_status: string
}

export enum TransactionStatusEnum {
  Pending = 'pending',
  Success = 'success',
  Failed = 'failed'
}

export enum TransactionDetailsStatusEnum {
  Active = 'active',
  Disable = 'disable',
  Expired = 'expired'
}

const modelConfig = {
  path: '/transaction/company',
  model: 'TRANSACTION'
}

export function useTransactionDetailStatusOptions() {
  const { __ } = useTranslator()

  return [
    { value: TransactionStatusEnum.Pending, label: __('Pending') },
    { value: TransactionStatusEnum.Success, label: __('Success') },
    { value: TransactionStatusEnum.Failed, label: __('Failed') }
  ]
}

export function useTransactionEmployeeStatusOptions() {
  const { __ } = useTranslator()

  return [
    { value: TransactionDetailsStatusEnum.Active, label: __('Active') },
    { value: TransactionDetailsStatusEnum.Disable, label: __('Disable') },
    { value: TransactionDetailsStatusEnum.Expired, label: __('Expired') }
  ]
}

export class Transaction extends Model {
  static queryKeys = {
    paginate: 'TRANSACTION_PAGINATE_QUERY',
    findOne: 'TRANSACTION_FIND_ONE_QUERY',
    getCompanyTransactions: 'TRANSACTION_COMPANY_QUERY',
    getgetCompanyTransactionsEmployeeByCompanyId: 'TRANSACTION_COMPANY_EMPLOYEE_QUERY_BY_ID',
    getgetCompanyTransactionsDetailByCompanyId: 'TRANSACTION_COMPANY_QUERY_BY_ID',
    transactionById: 'TRANSACTION_DETAIL_QUERY_BY_ID',
    exportCompanyTransactionsEmployeeByCompanyId: 'TRANSACTION_COMPANY_EMPLOYEE_EXPORT_QUERY_BY_ID'
  }

  static objects = ObjectsFactory.factory<ITransactions>(modelConfig, this.queryKeys)

  static getCompanyTransactions(params: any) {
    return {
      queryKey: [this.queryKeys.getCompanyTransactions, params],
      queryFn: () => {
        return this.api
          .get<IResponse<any>>({
            url: `${modelConfig.path}`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static getCompanyTransactionsEmployeeByCompanyId(id: any, params: any) {
    return {
      queryKey: [this.queryKeys.getgetCompanyTransactionsEmployeeByCompanyId, id, params],
      queryFn: () => {
        return this.api
          .get<IResponse<any>>({
            url: `${modelConfig.path}/${id}/employee`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static exportCompanyTransactionsEmployeeByCompanyId(id: any, params: any) {
    return {
      queryKey: [this.queryKeys.exportCompanyTransactionsEmployeeByCompanyId, id, params],
      queryFn: () => {
        return this.api.downloadFile(`${modelConfig.path}/${id}/employee`, 'transaction_export.xlsx', {
          params: { ...params, is_export: true }
        })
      }
    }
  }

  static getCompanyTransactionsDetailByCompanyId(params: any) {
    return {
      queryKey: [this.queryKeys.getgetCompanyTransactionsDetailByCompanyId, params],
      queryFn: () => {
        return this.api
          .get<IResponse<ITransactionsCompany>>({
            url: `${modelConfig.path}/${params.company_id}`,
            params
          })
          .then(r => r.data)
      }
    }
  }

  static getTransactionsDetailById(transaction_id: any) {
    return {
      queryKey: [this.queryKeys.transactionById],
      queryFn: () => {
        return this.api
          .get<IResponse<ITransactionDetail>>({
            url: `transaction/${transaction_id}`
          })
          .then(r => r.data.data)
      }
    }
  }

  static changeBulkEligibility(payload: IBulkEligibilityPayload) {
    return this.api.post<IResponse<any>>({
      url: `${modelConfig.path}/bulk-eligibility`,
      data: payload
    })
  }

  static changeBulkEligibilityById(id: any, payload: IBulkEligibilityPayload) {
    return this.api.post<IResponse<any>>({
      url: `${modelConfig.path}/${id}/bulk-eligibility`,
      data: payload
    })
  }

  static updateTransactionStatus(id: number, payload: ITransactionStatusPayload) {
    return this.api.put<IResponse<any>>({
      url: `transaction/employee/${id}/eligibility-status`,
      data: payload
    })
  }
}

Transaction.setup(modelConfig)

export function useChangeBulkEligibilityMutationById() {
  return useMutation<IResponse<any>, IHttpError, { id: any; payload: IBulkEligibilityPayload }>({
    mutationFn: ({ id, payload }) => {
      return Transaction.changeBulkEligibilityById(id, payload).then(r => r.data)
    }
  })
}

export function useChangeBulkEligibilityMutation() {
  return useMutation<IResponse<any>, IHttpError, { payload: IBulkEligibilityPayload }>({
    mutationFn: ({ payload }) => {
      return Transaction.changeBulkEligibility(payload).then(r => r.data)
    }
  })
}

export function useUpdateTransactionStatus() {
  return useMutation<IResponse<any>, IHttpError, { id: number; data: ITransactionStatusPayload }>({
    mutationFn: ({ id, data }) => {
      return Transaction.updateTransactionStatus(id, data).then(res => res.data)
    }
  })
}
